import type { TopClientRecord } from '../hooks/useTopClientsRevenue';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface ProcessedClient {
  contact_id: number;
  contact_name: string;
  client_base_currency: string;
  status: string;
  first_invoice_date: string;
  last_invoice_date: string;
  relationship_days: number;
  days_since_last_invoice: number;
  /** The resolved revenue in USD for the selected period */
  revenue: number;
  /** Revenue in the client's native currency */
  revenue_client_base: number;
  /** rank_total for lifetime mode; null in FY filter mode */
  rankValue: number | null;
  /** "FY2025-26 Q1" style label shown in FY quarter mode; null otherwise */
  fy_label: string | null;
  financial_year: string;
  revenue_period: string;
}

export interface KPIData {
  totalRevenue: number;
  topNRevenue: number;
  topNPercentage: number;
  top5Revenue: number;
  top5Percentage: number;
  clientCount: number;
  avgRevenuePerClient: number;
  riskLevel: 'High' | 'Medium' | 'Low';
}

export interface ConcentrationChartEntry {
  label: string;
  percentage: number;
  color: string;
}

// ─── Formatters ───────────────────────────────────────────────────────────────
export function fmtUSD(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-$' : '$';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${Math.round(abs).toLocaleString('en-US')}`;
}

export function fmtMoney(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

// ─── FY helpers ───────────────────────────────────────────────────────────────
/** Extract the 4-digit FY start year from strings like "FY2025-26", "2025", etc. */
export function parseFYStartYear(fy: string): number {
  const stripped = String(fy ?? '').replace(/^FY/i, '').trim();
  const m = stripped.match(/^(\d{4})/);
  if (m) return parseInt(m[1], 10);
  const n = parseInt(stripped, 10);
  return Number.isFinite(n) ? n : 0;
}

/** Returns unique FY year strings sorted descending (most recent first). */
export function getAvailableFYYears(records: TopClientRecord[]): string[] {
  const fySet = new Set<string>();
  records.forEach(r => {
    if (r.financial_year) fySet.add(r.financial_year);
  });
  return Array.from(fySet).sort((a, b) => {
    const ya = parseFYStartYear(a);
    const yb = parseFYStartYear(b);
    if (yb !== ya) return yb - ya;
    return b.localeCompare(a, undefined, { sensitivity: 'base' });
  });
}

export function getQuarterMonthLabel(quarter: string): string {
  switch (quarter) {
    case 'Q1': return 'Apr–Jun';
    case 'Q2': return 'Jul–Sep';
    case 'Q3': return 'Oct–Dec';
    case 'Q4': return 'Jan–Mar';
    default: return '';
  }
}

export function getPeriodLabel(
  selectedFYYear: string,
  selectedQuarter: string,
  isFYQuarterMode: boolean
): string {
  if (selectedFYYear === 'all') return 'Lifetime Revenue';
  if (isFYQuarterMode) {
    const months = getQuarterMonthLabel(selectedQuarter);
    return `${selectedFYYear} ${selectedQuarter}${months ? ` (${months})` : ''}`;
  }
  return selectedFYYear;
}

// ─── Core client processing ───────────────────────────────────────────────────
/**
 * Filters, deduplicates, and aggregates records based on selected FY / quarter.
 * Mirrors the v1 renderTopClientsRevenueWidget logic.
 */
export function processClients(
  records: TopClientRecord[],
  selectedFYYear: string,
  selectedQuarter: string
): {
  clients: ProcessedClient[];
  usedFallback: boolean;
  isFYQuarterMode: boolean;
} {
  const hasFYFilters = (selectedFYYear !== 'all') || (selectedQuarter !== 'all');
  const isFYQuarterMode = (selectedFYYear !== 'all') && (selectedQuarter !== 'all');

  if (hasFYFilters) {
    // Filter rows matching the selected FY and/or quarter
    const filtered = records.filter(r => {
      const matchesFY = selectedFYYear === 'all' || r.financial_year === selectedFYYear;
      const matchesQ = selectedQuarter === 'all' || r.revenue_period === selectedQuarter;
      return matchesFY && matchesQ;
    });

    // Aggregate by contact_id: sum revenue_fy_quarter across matching rows
    const clientMap = new Map<number, ProcessedClient>();
    filtered.forEach(r => {
      if (r.revenue_fy_quarter <= 0) return;
      const existing = clientMap.get(r.contact_id);
      if (existing) {
        existing.revenue += r.revenue_fy_quarter;
        existing.revenue_client_base += r.revenue_fy_quarter_client_base;
      } else {
        clientMap.set(r.contact_id, {
          contact_id: r.contact_id,
          contact_name: r.contact_name,
          client_base_currency: r.client_base_currency,
          status: r.status,
          first_invoice_date: r.first_invoice_date,
          last_invoice_date: r.last_invoice_date,
          relationship_days: r.relationship_days,
          days_since_last_invoice: r.days_since_last_invoice,
          revenue: r.revenue_fy_quarter,
          revenue_client_base: r.revenue_fy_quarter_client_base,
          rankValue: null,  // no pre-computed rank for FY quarter aggregation
          fy_label: `${r.financial_year} ${r.revenue_period}`,
          financial_year: r.financial_year,
          revenue_period: r.revenue_period,
        });
      }
    });

    let clients = Array.from(clientMap.values())
      .filter(c => c.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);

    if (clients.length > 0) {
      return { clients, usedFallback: false, isFYQuarterMode };
    }

    // Fallback: no results for the selected FY/Q → use lifetime revenue
    const fallback = buildTotalModeClients(records);
    return { clients: fallback, usedFallback: true, isFYQuarterMode: false };
  }

  // No FY filters: use revenue_total, deduplicate keeping highest value per client
  const clients = buildTotalModeClients(records);
  return { clients, usedFallback: false, isFYQuarterMode: false };
}

function buildTotalModeClients(records: TopClientRecord[]): ProcessedClient[] {
  const clientMap = new Map<number, ProcessedClient>();
  records.forEach(r => {
    if (r.revenue_total <= 0) return;
    const existing = clientMap.get(r.contact_id);
    if (existing) {
      // Keep the row with the highest revenue_total
      if (r.revenue_total > existing.revenue) {
        existing.revenue = r.revenue_total;
        existing.revenue_client_base = r.revenue_total_client_base;
        existing.rankValue = r.rank_total;
        // Keep the metadata from the most-revenue row
        existing.first_invoice_date = r.first_invoice_date;
        existing.last_invoice_date = r.last_invoice_date;
        existing.relationship_days = r.relationship_days;
        existing.days_since_last_invoice = r.days_since_last_invoice;
      }
    } else {
      clientMap.set(r.contact_id, {
        contact_id: r.contact_id,
        contact_name: r.contact_name,
        client_base_currency: r.client_base_currency,
        status: r.status,
        first_invoice_date: r.first_invoice_date,
        last_invoice_date: r.last_invoice_date,
        relationship_days: r.relationship_days,
        days_since_last_invoice: r.days_since_last_invoice,
        revenue: r.revenue_total,
        revenue_client_base: r.revenue_total_client_base,
        rankValue: r.rank_total,
        fy_label: null,
        financial_year: r.financial_year,
        revenue_period: r.revenue_period,
      });
    }
  });

  return Array.from(clientMap.values())
    .filter(c => c.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue);
}

// ─── KPI computation ──────────────────────────────────────────────────────────
export function computeKPIs(sortedClients: ProcessedClient[], topN: number): KPIData {
  const totalRevenue = sortedClients.reduce((s, c) => s + c.revenue, 0);
  const topNClients = sortedClients.slice(0, topN);
  const topNRevenue = topNClients.reduce((s, c) => s + c.revenue, 0);
  const topNPercentage = totalRevenue > 0 ? (topNRevenue / totalRevenue) * 100 : 0;

  // Top 5 within the top-N slice (matching v1 behaviour)
  const top5Slice = topNClients.slice(0, Math.min(5, topNClients.length));
  const top5Revenue = top5Slice.reduce((s, c) => s + c.revenue, 0);
  const top5Percentage = totalRevenue > 0 ? (top5Revenue / totalRevenue) * 100 : 0;

  const clientCount = sortedClients.length;
  const avgRevenuePerClient = clientCount > 0 ? totalRevenue / clientCount : 0;
  const riskLevel: 'High' | 'Medium' | 'Low' =
    topNPercentage > 80 ? 'High' : topNPercentage > 60 ? 'Medium' : 'Low';

  return {
    totalRevenue,
    topNRevenue,
    topNPercentage,
    top5Revenue,
    top5Percentage,
    clientCount,
    avgRevenuePerClient,
    riskLevel,
  };
}

// ─── Chart data ───────────────────────────────────────────────────────────────
export function buildConcentrationChartData(
  kpis: KPIData,
  topN: number,
  sortedClients: ProcessedClient[]
): ConcentrationChartEntry[] {
  const top1Revenue = sortedClients[0]?.revenue ?? 0;
  const top1Pct = kpis.totalRevenue > 0 ? (top1Revenue / kpis.totalRevenue) * 100 : 0;
  return [
    { label: 'Top 1',         percentage: top1Pct,              color: '#F59E0B' },
    { label: 'Top 5',         percentage: kpis.top5Percentage,  color: '#8B5CF6' },
    { label: `Top ${topN}`,   percentage: kpis.topNPercentage,  color: '#3B82F6' },
    { label: 'Remaining',     percentage: Math.max(0, 100 - kpis.topNPercentage), color: '#64748B' },
  ];
}

// ─── Relationship duration ────────────────────────────────────────────────────
export function formatRelationshipDuration(days: number): string {
  if (!Number.isFinite(days) || days < 0) return '';
  const years = Math.floor(days / 365);
  const rem = days % 365;
  const months = Math.floor(rem / 30);
  const d = rem % 30;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (d > 0 || parts.length === 0) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
  if (parts.length === 1) return parts[0];
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
}

// ─── Activity summary ─────────────────────────────────────────────────────────
export interface ActivitySummary {
  dateRange: string;
  activeCount: number;
  inactiveCount: number;
}

export function getActivitySummary(sortedClients: ProcessedClient[]): ActivitySummary {
  const dates = sortedClients
    .filter(c => c.first_invoice_date && c.last_invoice_date)
    .flatMap(c => [new Date(c.first_invoice_date), new Date(c.last_invoice_date)])
    .filter(d => !isNaN(d.getTime()))
    .map(d => d.getTime());

  let dateRange = 'No data';
  if (dates.length > 0) {
    const earliest = new Date(Math.min(...dates)).toLocaleDateString();
    const latest = new Date(Math.max(...dates)).toLocaleDateString();
    dateRange = `${earliest} to ${latest}`;
  }

  // A client is "active" if last_invoice_date is in 2024 or later (matches v1 heuristic)
  const activeCount = sortedClients.filter(c => {
    if (!c.last_invoice_date) return false;
    return new Date(c.last_invoice_date).getFullYear() >= 2024;
  }).length;
  const inactiveCount = sortedClients.length - activeCount;

  return { dateRange, activeCount, inactiveCount };
}
